<script setup lang="ts">
/**
 * Which card a payment goes on: one already saved, or a different one.
 *
 * None saved and the new card's form, in the slot, is all there is. Exactly
 * one and it is simply named: being made to re-enter a card the site already
 * holds reads as though it has forgotten them. More than one and they choose.
 */
import { Check, CreditCard } from 'lucide-vue-next'
import { NEW_CARD, type SavedCard } from '@/lib/savedCards'

defineProps<{
  cards: SavedCard[]
}>()

/** A saved card's id, NEW_CARD, or null while nothing is chosen. */
const chosen = defineModel<string | null>({ required: true })
</script>

<template>
  <div class="space-y-3">
    <section v-if="cards.length" class="space-y-3">
      <h3 class="font-semibold text-gray-900">
        {{ cards.length === 1 ? 'Paying with' : 'Which card?' }}
      </h3>

      <button
        v-for="card in cards"
        :key="card.id"
        type="button"
        :aria-pressed="chosen === card.id"
        class="flex w-full items-center justify-between rounded-xl border-2 p-4 text-left transition-colors"
        :class="chosen === card.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'"
        @click="chosen = card.id"
      >
        <span class="flex items-center gap-3">
          <CreditCard class="h-5 w-5 text-gray-400" aria-hidden="true" />
          <span>
            <span class="font-medium text-gray-900">{{ card.brand }} ending {{ card.last_four }}</span>
            <span class="block text-sm text-gray-500">Expires {{ card.expires }}</span>
          </span>
        </span>
        <Check v-if="chosen === card.id" class="h-5 w-5 text-blue-600" aria-hidden="true" />
      </button>

      <button
        v-if="chosen !== NEW_CARD"
        type="button"
        class="text-sm font-medium text-blue-600 hover:text-blue-700"
        @click="chosen = NEW_CARD"
      >
        Use a different card
      </button>
    </section>

    <slot v-if="chosen === NEW_CARD" />
  </div>
</template>
